import { useEffect, useState, useRef, useMemo } from 'react'
import { Star, ChevronsUpDown } from 'lucide-react'
import { CardHeader, CardTitle } from '@/components/ui/card.tsx'
import { useTheme } from '@/components/ui/theme-provider.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TooltipPortal,
} from '@/components/ui/tooltip.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Input } from '@/components/ui/input.tsx'
import { useAnimate } from 'framer-motion'
import { useGraphStore } from '@/state/index.ts'
import { LabelSelect } from '@/components/labels/label-select.tsx'
import { LabelBadge } from '@/components/labels/label-badge.tsx'
import { v4 as uuidv4 } from 'uuid'
import { DeleteQuery } from '@/components/queries/delete-query.tsx'

import type { ChangeEvent } from 'react'
import type { QueryLabel, SavedQuery } from '@/state/types.ts'

interface SaveQueryPopoverProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

const SaveQueryButton = () => {
  const [showSaveQueryPopover, setShowSaveQueryPopover] =
    useState<boolean>(false)
  const [scope, animate] = useAnimate()
  const savedQueries = useGraphStore(state => state.savedQueries)
  const activeQuery = useGraphStore(state => state.query)
  const { resolvedTheme } = useTheme()

  /** Once the save button is clicked, the query is saved. */
  useEffect(() => {
    animate(scope.current, {
      rotate: showSaveQueryPopover ? -71.5 : 0,
    })
  }, [showSaveQueryPopover, animate, scope])

  const starColor = useMemo(() => {
    const foundQuery = savedQueries?.find(
      query => query.query === activeQuery,
    )
    return foundQuery && resolvedTheme === 'dark' ?
        '#fafafa'
      : '#212121'
  }, [savedQueries, activeQuery, resolvedTheme])

  return (
    <Popover
      open={showSaveQueryPopover}
      onOpenChange={setShowSaveQueryPopover}>
      <PopoverTrigger className="flex items-center justify-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              asChild
              className="dark:bg-muted flex size-6 cursor-pointer items-center justify-center rounded-md border border-neutral-200 bg-neutral-100 transition-colors hover:bg-neutral-300 dark:border-neutral-700 dark:hover:bg-neutral-700">
              <div
                onClick={() =>
                  setShowSaveQueryPopover(!showSaveQueryPopover)
                }
                className="border-input bg-background ring-offset-background hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring inline-flex size-5 items-center justify-center gap-2 rounded-md border text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-[15px] [&_svg]:shrink-0">
                <Star ref={scope} fill={starColor} />
              </div>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent>
                <p>Save query</p>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
      </PopoverTrigger>
      <PopoverContent className="w-full rounded-xl p-0" align="end">
        <SaveQueryPopover
          isOpen={showSaveQueryPopover}
          setIsOpen={setShowSaveQueryPopover}
        />
      </PopoverContent>
    </Popover>
  )
}

const SaveQueryPopover = ({
  isOpen,
  setIsOpen,
}: SaveQueryPopoverProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] =
    useState<boolean>(false)
  const [_labelSelectPopoverOpen, setLabelSelectPopoverOpen] =
    useState<boolean>(false)
  const hasRun = useRef<boolean>(false)

  const [savedQuery, setSavedQuery] = useState<SavedQuery | null>(
    null,
  )
  const [queryName, setQueryName] = useState<string>('')
  const [editContext, setEditContext] = useState<string>('')
  const [selectedLabels, setSelectedLabels] = useState<QueryLabel[]>(
    [],
  )

  const saveQuery = useGraphStore(state => state.saveQuery)
  const nodes = useGraphStore(state => state.nodes)
  const updateQuery = useGraphStore(state => state.updateSavedQuery)
  const savedQueries = useGraphStore(state => state.savedQueries)
  const activeQuery = useGraphStore(state => state.query)

  useEffect(() => {
    // React runs useEffect twice in strict mode during dev
    // This ensures that this useEffect only ever runs once.
    if (!hasRun.current) {
      hasRun.current = true

      const foundQuery = savedQueries?.find(
        query => query.query === activeQuery,
      )
      setSavedQuery(foundQuery ?? null)

      // If the query exists, update it.
      if (foundQuery && queryName !== '') {
        setSelectedLabels(foundQuery.labels ?? [])
        setQueryName(
          foundQuery.name || nodes[0]?.manifest?.name || '',
        )
        setEditContext(
          foundQuery.context || nodes[0]?.projectRoot || '',
        )
        const item: SavedQuery = {
          ...foundQuery,
          name: queryName,
          context: editContext,
          query: activeQuery,
          labels: selectedLabels,
          dateModified: new Date().toISOString(),
        }
        updateQuery(item)
      }

      // If the query doesnt exist, save it.
      if (!foundQuery) {
        saveQuery({
          id: uuidv4(),
          name: nodes[0]?.manifest?.name ?? 'Query',
          context: nodes[0]?.projectRoot ?? '',
          query: activeQuery,
          labels: selectedLabels,
          dateCreated: new Date().toISOString(),
          dateModified: new Date().toISOString(),
        })
      }
    }
  }, [
    activeQuery,
    nodes,
    saveQuery,
    savedQueries,
    updateQuery,
    editContext,
    queryName,
    selectedLabels,
  ])

  /**
   * Set the default state of the text inputs
   */
  useEffect(() => {
    if (!isOpen) {
      const foundQuery = savedQueries?.find(
        query => query.query === activeQuery,
      )
      setSavedQuery(foundQuery ?? null)

      if (foundQuery && queryName !== '') {
        setSelectedLabels(foundQuery.labels ?? [])
        setQueryName(
          foundQuery.name || nodes[0]?.manifest?.name || '',
        )
        setEditContext(
          foundQuery.context || nodes[0]?.projectRoot || '',
        )
        const item: SavedQuery = {
          ...foundQuery,
          name: queryName,
          context: editContext,
          query: activeQuery,
          labels: selectedLabels,
          dateModified: new Date().toISOString(),
        }
        updateQuery(item)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- causes infinite loop
  }, [
    isOpen,
    activeQuery,
    nodes,
    updateQuery,
    editContext,
    queryName,
    selectedLabels,
  ])

  /**
   * Update the UI inputs
   */
  useEffect(() => {
    setSelectedLabels(savedQuery?.labels ?? [])
    setQueryName(savedQuery?.name ?? nodes[0]?.manifest?.name ?? '')
    setEditContext(savedQuery?.context ?? nodes[0]?.projectRoot ?? '')
  }, [savedQuery, nodes])

  // exit early to avoid UI flash
  if (!isOpen) return null

  return (
    <div className="flex w-[325px] flex-col">
      <CardHeader className="px-6 py-3">
        <CardTitle className="text-md font-medium">
          {savedQuery ? 'Edit Query' : 'Added Query!'}
        </CardTitle>
      </CardHeader>
      <div className="border-muted-foreground/20 flex flex-col gap-2 border-t-[1px] px-6 py-4">
        <Label className="border-none p-0 font-medium">Name</Label>
        <Input
          type="text"
          autoComplete="off"
          placeholder="Name"
          className="rounded-lg"
          value={queryName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const value = e.currentTarget.value
            setQueryName(value)
          }}
        />
        <Label className="mt-2 border-none p-0 font-medium">
          Directory
        </Label>
        <Input
          id="query-context"
          type="text"
          autoComplete="off"
          className="rounded-lg"
          placeholder="Directory (optional)"
          value={editContext}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const value = e.currentTarget.value
            setEditContext(value)
          }}
        />
        <Label className="mt-2 border-none p-0 font-medium">
          Labels
        </Label>
        {selectedLabels.length !== 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedLabels.map((label, idx) => (
              <LabelBadge
                key={idx}
                name={label.name}
                color={label.color}
              />
            ))}
          </div>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-full justify-between rounded-lg font-normal">
              Select labels
              <ChevronsUpDown className="opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0">
            <LabelSelect
              selectedItems={selectedLabels}
              setItems={setSelectedLabels}
              setIsOpen={setLabelSelectPopoverOpen}
            />
          </PopoverContent>
        </Popover>
        <div className="mt-4 flex w-full items-center justify-end gap-2">
          <div className="flex items-center gap-3">
            {savedQuery && (
              <DeleteQuery
                type="button"
                text="Remove"
                className="rounded-lg"
                deleteDialogOpen={deleteDialogOpen}
                setDeleteDialogOpen={setDeleteDialogOpen}
                onClose={() => setIsOpen(false)}
                selectedQueries={[
                  {
                    ...savedQuery,
                    name: queryName,
                    context: editContext,
                    query: activeQuery,
                    labels: selectedLabels,
                  },
                ]}
              />
            )}
            <Button
              size="sm"
              className="rounded-lg"
              onClick={() => setIsOpen(false)}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SaveQueryButton
